import React, { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ClickAwayListener,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
  Unstable_Grid2 as Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { useFormikContext } from 'formik';

import { useReleases } from 'hooks';
import { GroupContentApprovalType, UpdateGroupInput } from 'api';
import theme from 'theme';
import { useGroupModerators } from 'features/groups/hooks';

const styles = {
  button: {
    backgroundColor: theme.palette.grey[100],
    borderRadius: 0.5,
  },
  popper: {
    zIndex: 9000,
    minWidth: 200,
  },
  item: {
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
    },
  },
} as const;

const ModerationType: FC = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<UpdateGroupInput>();
  const { moderators } = useGroupModerators();
  const releases = useReleases();

  const has4EyeModeration = releases.includes('4EyeModeration');
  const anchorEl = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    if (values.content_approval_type === GroupContentApprovalType.FourEyes) {
      setIsLocked(true);
    }
  }, []);

  function getEndIcon() {
    if (isLocked) {
      return <LockRoundedIcon />;
    }

    return open ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />;
  }

  function handleToggle() {
    setOpen(prevOpen => !prevOpen);
  }

  function handleClose() {
    setOpen(false);
  }

  function onClickMenuItem(event: MouseEvent<HTMLLIElement>, value: GroupContentApprovalType) {
    handleClose();
    setFieldValue('content_approval_type', value);
  }

  function renderContent() {
    let title = null;
    let description = null;

    switch (values.content_approval_type) {
      case GroupContentApprovalType.Auto:
        title = t('groups:noModeration');
        description = t('groups:noModerationDescription');
        break;
      case GroupContentApprovalType.Review:
        title = t('groups:moderation');
        description = t('groups:needModerationDescription');
        break;
      case GroupContentApprovalType.FourEyes:
        title = t('groups:fourEyeModeration');
        description = t('groups:fourEyeModerationDescription');
        break;
      default:
        title = t('groups:noModeration');
        description = t('groups:noModerationDescription');
        break;
    }

    return (
      <Box textAlign="right" paddingLeft={2} paddingRight={1}>
        <Typography variant="body2" color="black">
          {title}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {description}
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid xs={12} sm={4}>
        <Typography variant="body2" display="inline">
          {t('groups:moderationType')}
        </Typography>
      </Grid>
      <Grid xs={12} sm container justifyContent="flex-end" alignItems="flex-end">
        <Grid xsOffset="auto">
          <Button
            variant="contained"
            ref={anchorEl}
            sx={{
              ...styles.button,
              '&.Mui-disabled': {
                backgroundColor: theme.palette.common.white,
              },
            }}
            endIcon={getEndIcon()}
            onClick={handleToggle}
            disabled={isLocked}
          >
            {renderContent()}
          </Button>
          <Popper
            anchorEl={anchorEl.current}
            open={open}
            placement="bottom-end"
            disablePortal
            sx={styles.popper}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem={open}>
                  <MenuItem
                    value={GroupContentApprovalType.Auto}
                    onClick={e => onClickMenuItem(e, GroupContentApprovalType.Auto)}
                    sx={styles.item}
                  >
                    {t('groups:noModeration')}
                  </MenuItem>
                  <MenuItem
                    value={GroupContentApprovalType.Review}
                    onClick={e => onClickMenuItem(e, GroupContentApprovalType.Review)}
                    sx={styles.item}
                  >
                    {t('groups:moderation')}
                  </MenuItem>
                  {has4EyeModeration && moderators.length >= 3 && (
                    <MenuItem
                      value={GroupContentApprovalType.FourEyes}
                      onClick={e => onClickMenuItem(e, GroupContentApprovalType.FourEyes)}
                      sx={styles.item}
                    >
                      {t('groups:fourEyeModeration')}
                    </MenuItem>
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Popper>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ModerationType;
